"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ViewSettingsI18nError = exports.ViewSettingsI18nSchema = exports.ViewAiSettingsSchema = exports.AiContentDeclarationSchema = exports.ViewSeoI18nSchema = exports.TwitterCardTypeSchema = exports.JsonLdTypeSchema = exports.I18nStringMapSchema = exports.LocaleTagSchema = void 0;
exports.assertViewSettingsI18n = assertViewSettingsI18n;
exports.resolveI18nValue = resolveI18nValue;
/**
 * sl-y0sj.2: ViewSettingsI18n — multi-locale metadata for the view-level
 * settings blob that lives on `AppBase.config.settings` (and is
 * frozen onto `MaterializedViewSnapshot.data.uiConfig.settings` at publish
 * time).
 *
 * Per-locale fields live in the snapshot so the public renderer can serve
 * crawler-friendly localized HTML without a Y.Doc round-trip. Validation
 * enforces that:
 *   1. `defaultLocale` is one of `availableLocales`.
 *   2. Every I18n map carries a value for EVERY available locale (no
 *      partial translations leaking blanks into HTML).
 *
 * BCP-47 tag format (`de`, `de-DE`, `zh-Hant`) — RFC 5646. Validation
 * is structural (non-empty short string), not full RFC 5646 parsing —
 * the editor UI gates input.
 */
const zod_1 = require("zod");
const BCP47_TAG_RE = /^[a-zA-Z]{2,3}(-[A-Za-z0-9]{2,8})*$/;
exports.LocaleTagSchema = zod_1.z
    .string()
    .min(2)
    .max(35)
    .regex(BCP47_TAG_RE, 'Invalid BCP-47 locale tag');
/**
 * `Record<LocaleTag, string>` — keys are locales, values are localized strings.
 * Zod's `z.record(z.string(), z.string())` is the closest practical encoding;
 * BCP-47 key validation happens via `assertCompleteI18n` below.
 */
exports.I18nStringMapSchema = zod_1.z.record(zod_1.z.string(), zod_1.z.string());
/**
 * SEO-block metadata as it lives inside `view.config.settings.seo`. All
 * fields are optional because older AppBases may not have set
 * them — the schema only kicks in when present, validation in
 * `assertViewSettingsI18n` covers completeness once `availableLocales`
 * is set.
 */
/**
 * sl-y0sj.3: SERP-SEO-Pack — schema.org type tag for JSON-LD emission.
 * `auto` lets the renderer pick by layoutType (Article for article views,
 * Dataset for tables, WebPage for forms, BreadcrumbList+Article for row
 * details).
 */
exports.JsonLdTypeSchema = zod_1.z.enum([
    'auto',
    'article',
    'dataset',
    'webpage',
]);
exports.TwitterCardTypeSchema = zod_1.z.enum(['summary', 'summary_large_image']);
exports.ViewSeoI18nSchema = zod_1.z
    .object({
    titlesI18n: exports.I18nStringMapSchema.optional(),
    descriptionsI18n: exports.I18nStringMapSchema.optional(),
    ogImagesI18n: exports.I18nStringMapSchema.optional(),
    indexing: zod_1.z.boolean().optional(),
    /**
     * sl-y0sj.3: Optional override for the `<link rel="canonical">` URL.
     * Defaults to the current request URL. Used when a view is also
     * surfaced under a marketing path (e.g. duplicated content).
     */
    canonicalUrlOverride: zod_1.z.string().url().optional(),
    /** sl-y0sj.3: schema.org type-tag for the JSON-LD block. */
    jsonLdType: exports.JsonLdTypeSchema.optional(),
    /** sl-y0sj.3: Twitter Card layout. Default `summary_large_image`. */
    twitterCardType: exports.TwitterCardTypeSchema.optional(),
})
    .passthrough();
/**
 * sl-y0sj.4: AIEO-Pack — `ai-content-declaration` meta declaration value.
 * Aligned with EU AI Act Art. 50(2) provider-disclosure values plus
 * `undeclared` (suppresses the meta tag). The HTML emitter omits the tag
 * when the field is unset OR `undeclared`.
 */
exports.AiContentDeclarationSchema = zod_1.z.enum([
    'human-authored',
    'ai-generated',
    'ai-assisted',
    'mixed',
    'undeclared',
]);
/** sl-y0sj.4: View-level AI block. Extensible — declaration is the only field today. */
exports.ViewAiSettingsSchema = zod_1.z
    .object({
    declaration: exports.AiContentDeclarationSchema.optional(),
})
    .passthrough();
/**
 * Locale-aware portion of `view.config.settings`. Coexists with theme/layout
 * blocks (kept as `unknown` passthrough) so legacy fields are not dropped.
 */
exports.ViewSettingsI18nSchema = zod_1.z
    .object({
    availableLocales: zod_1.z.array(exports.LocaleTagSchema).min(1).default(['en']),
    defaultLocale: exports.LocaleTagSchema.default('en'),
    seo: exports.ViewSeoI18nSchema.optional(),
    /** sl-y0sj.4: AIEO-Pack — view-level AI provenance declaration. */
    ai: exports.ViewAiSettingsSchema.optional(),
})
    .passthrough();
class ViewSettingsI18nError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ViewSettingsI18nError';
    }
}
exports.ViewSettingsI18nError = ViewSettingsI18nError;
/**
 * Validate semantic invariants that pure schema validation cannot express:
 *   - `defaultLocale` must be a member of `availableLocales`.
 *   - Each I18n map must cover every available locale (no partial-i18n).
 *
 * Throws `ViewSettingsI18nError` with a precise message — caller surfaces
 * to the user (publish wizard, GraphQL validation pipe).
 */
function assertViewSettingsI18n(input) {
    const { availableLocales, defaultLocale, seo } = input;
    if (!availableLocales.includes(defaultLocale)) {
        throw new ViewSettingsI18nError(`defaultLocale "${defaultLocale}" must be present in availableLocales [${availableLocales.join(', ')}]`);
    }
    if (!seo)
        return;
    for (const [field, map] of Object.entries({
        titlesI18n: seo.titlesI18n,
        descriptionsI18n: seo.descriptionsI18n,
        ogImagesI18n: seo.ogImagesI18n,
    })) {
        if (!map)
            continue;
        for (const locale of availableLocales) {
            const value = map[locale];
            if (typeof value !== 'string' || value.length === 0) {
                throw new ViewSettingsI18nError(`seo.${field} is missing a value for locale "${locale}" — partial-i18n is not allowed`);
            }
        }
    }
}
/**
 * Resolve the value of an I18n map for a given locale, falling back to
 * the default locale if the requested locale has no entry. Returns
 * `undefined` if even the default locale is absent (caller decides whether
 * that is an error or a "leave field unset" signal).
 */
function resolveI18nValue(map, requestedLocale, defaultLocale) {
    if (!map)
        return undefined;
    return map[requestedLocale] ?? map[defaultLocale];
}
